import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getV2Token, optimizeV2CookiesBeforeNavigation } from '../utils/v2AuthUtils';
import SecureCookieManager from '../utils/secureCookie';
import { validateToken } from '../utils/tokenValidator';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute Component
 * 
 * Uses the same authentication mechanism as new_demo_frontend:
 * - Reads token from v2-frontend cookie format (d_c_c_t_p_1, d_c_c_t_p_2, etc.)
 * - Validates token with backend
 * - Protects routes from unauthorized access
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const [status, setStatus] = useState<'checking' | 'valid' | 'invalid'>('checking');
  const location = useLocation();
  const isRedirecting = useRef(false);

  useEffect(() => {
    // Prevent multiple simultaneous redirects
    if (isRedirecting.current) {
      return;
    }

    const runValidation = async (retries = 3) => {
      if ((window as any).__LOGGING_OUT__) {
        setStatus('invalid');
        return;
      }

      try {
        // Optimize cookies FIRST to prevent "Request Header Or Cookie Too Large" errors
        // This clears any orphaned/duplicate cookies before we read or set new ones
        optimizeV2CookiesBeforeNavigation();
        
        // Get token from v2-frontend cookie format (same as new_demo_frontend)
        const token = getV2Token();
        
        if (!token) {
          setStatus('invalid');
          return;
        }

        // Validate token client-side (same as new_demo_frontend)
        // This decodes the JWT and extracts user info without backend call
        const userData = validateToken(token);
        
        if (userData) {
          // Only set front_compliance's own cookies if they don't already exist
          // This prevents duplicate cookies that increase header size
          const existingToken = SecureCookieManager.getAuthToken();
          if (!existingToken || existingToken !== token) {
            SecureCookieManager.setAuthToken(token);
          }
          
          // Only set user data if it doesn't exist or has changed
          const existingUserData = SecureCookieManager.getUserData();
          if (!existingUserData || JSON.stringify(existingUserData) !== JSON.stringify(userData)) {
            SecureCookieManager.setUserData(userData);
          }
          
          setStatus('valid');
        } else {
          // Token validation failed
          SecureCookieManager.clearAuthCookies();
          setStatus('invalid');
        }
      } catch (e) {
        console.error('[ProtectedRoute] Validation error:', e);
        if (retries > 0) {
          setTimeout(() => runValidation(retries - 1), 150);
        } else {
          SecureCookieManager.clearAuthCookies();
          setStatus('invalid');
        }
      }
    };

    if (status === 'checking') {
      runValidation();
    }
  }, [status, location.pathname]);

  if (status === 'invalid') {
    if (!isRedirecting.current) {
      isRedirecting.current = true;
      setTimeout(() => {
        isRedirecting.current = false;
      }, 1000);
    }
    return <Navigate to="/login" replace />;
  }

  if (status === 'checking') {
    return <div>Loading...</div>;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 